import type { Settings } from '../types';

/**
 * Settings → CSS custom properties.
 *
 * Everything the person touches is sized in millimetres and converted through
 * `--px-per-mm` (INV-08). A hard-coded 120 px key is 23 mm on this 264 ppi
 * iPad but 18.7 mm on a 326 ppi mini, so pixels would silently ship a worse device on
 * different hardware.
 *
 * Pure on purpose: this is the function that decides how big a target is for someone whose
 * motor control we cannot measure remotely, so it is tested rather than eyeballed.
 */

/**
 * The vertical band heights, in pt, from the Phase 1 plan §0.1.
 *
 * 132 + 104 + 180 + 4 × 8 = 448 fixed, leaving the keyboard **320 pt** of the 768 usable on an
 * Air 4. That residual is what caps key height at 19.5 mm, whatever the column count.
 * **Measure this on the device; the arithmetic is not the answer.**
 *
 * `bottomRow` is 84, not the 83 the plan rounded to: 16 mm × 5.20 px/mm is 83.2, and a row of 83
 * would be 15.96 mm — under the floor it is supposed to define. Found by the geometry guard in
 * `tests/flow/surfaces.spec.ts`, which is exactly the kind of 0.2 mm nobody catches by eye.
 */
export const BAND_HEIGHTS = {
  output: 132,
  prediction: 104,
  bottomRow: 84,
  divider: 12,
  gap: 8,
} as const;

/** Usable height on the target device: 820 pt viewport less 32 top and 20 bottom safe area. */
export const AIR4_USABLE_HEIGHT = 768;

/**
 * The interactive floor, mirroring `--min-target-mm` in `tokens.css`.
 *
 * Duplicated across a CSS file and a TypeScript one, which is the same shape of hazard `LAYOUT_COLUMNS` has
 * against the layout JSON — so `tests/unit/accessibility.test.ts` reads the token and compares (`GEO-07`).
 */
export const MIN_TARGET_MM = 16;

/** What is left for the keyboard once the fixed bands are taken. */
export function keyboardBandHeight(usableHeight = AIR4_USABLE_HEIGHT): number {
  const fixed =
    BAND_HEIGHTS.output +
    BAND_HEIGHTS.prediction +
    (2 * BAND_HEIGHTS.bottomRow + BAND_HEIGHTS.divider) +
    4 * BAND_HEIGHTS.gap;
  return usableHeight - fixed;
}

export function settingsToCssVars(settings: Settings): Record<string, string> {
  return {
    '--px-per-mm': String(settings.pxPerMm),
    '--key-mm': String(settings.keyMm),
    '--key-gap-mm': String(settings.keyGapMm),
    '--content-band-pct': String(settings.contentBandPct),
    '--content-band-offset-pct': String(settings.contentBandOffsetPct),
    '--font-scale': String(settings.fontScale),
    '--focus-ring': `${settings.focusRingPx}px`,
  };
}

export function applyCssVars(target: HTMLElement, vars: Record<string, string>): void {
  for (const [name, value] of Object.entries(vars)) target.style.setProperty(name, value);
}

export function applyTheme(target: HTMLElement, theme: Settings['theme']): void {
  target.dataset.theme = theme;
}

/**
 * How many columns each shipped layout needs. `keyMm` is only satisfiable up to a ceiling.
 *
 * This duplicates each layout's own `columns` field, and nothing at runtime reconciles the two — a
 * disagreement here sizes the keys for one grid and draws another. `abc` sat at 10 for months while
 * it was an unbuilt placeholder and was wrong the moment it shipped as an 11-column board, so
 * `tests/unit/layouts.test.ts` now asserts the two agree for every shipped layout.
 */
export const LAYOUT_COLUMNS: Record<Settings['keyboardLayout'], number> = {
  'de-CH': 11,
  'de-CH-10col': 10,
  abc: 11,
  'abc-10col': 10,
  frequency: 10,
};

export interface KeyboardMetrics {
  /** Square key edge in px. */
  keyPx: number;
  /** Horizontal gap, grown to spread the grid across the full width. */
  gapXPx: number;
  /** Vertical gap. Never grown — vertical space is the scarce dimension. */
  gapYPx: number;
  /** Which constraint bound the size, so care mode can explain itself. */
  clampedBy: 'none' | 'width' | 'height';
  /** The largest `keyMm` this layout can actually honour. */
  maxKeyMm: number;
}

/**
 * Key geometry, which is **not** simply `keyMm × pxPerMm` — it is bound by both dimensions, and
 * which one binds depends on the layout.
 *
 *  - **11 columns** (default): width binds at **19.27 mm**. The 19 mm default sits 0.27 mm below
 *    it, so raising `keyMm` at this layout does essentially nothing and Betreuer-Modus must say so
 *    rather than appear to ignore the drag.
 *  - **10 columns**: width allows 21.34 mm but **height binds at 19.62 mm**, because the frame
 *    leaves the keyboard 322 pt for three rows. So the ten-column layout gains 0.35 mm, not the
 *    1 mm the Phase 0 findings assumed — 20 mm keys need 328 pt and do not fit.
 *
 * What ten columns actually buy is **gap**: leftover horizontal space is distributed into
 * `gapXPx` rather than left as a margin, so the same key size spread over 1180 pt sits 3.4 mm
 * apart instead of 1.5 mm. The design credits gap with preventing mis-hits as much as size does,
 * and calibration distinguishes the two failures — misses want bigger keys, wrong-key hits want
 * bigger gaps.
 *
 * Keys stay square so that muscle memory has one shape to learn.
 */
export function keyboardMetrics(args: {
  keyMm: number;
  keyGapMm: number;
  pxPerMm: number;
  availableWidthPx: number;
  availableHeightPx: number;
  columns: number;
  rows: number;
}): KeyboardMetrics {
  const { keyMm, keyGapMm, pxPerMm, availableWidthPx, availableHeightPx, columns, rows } = args;

  const minGapPx = keyGapMm * pxPerMm;
  const requestedPx = keyMm * pxPerMm;

  const fitWidthPx = (availableWidthPx - minGapPx * Math.max(0, columns - 1)) / columns;
  const fitHeightPx = (availableHeightPx - minGapPx * Math.max(0, rows - 1)) / rows;

  const keyPx = Math.min(requestedPx, fitWidthPx, fitHeightPx);

  let clampedBy: KeyboardMetrics['clampedBy'] = 'none';
  if (keyPx < requestedPx) clampedBy = fitWidthPx <= fitHeightPx ? 'width' : 'height';

  // Spread the grid across the whole width — keys are recall-driven and may span the screen
  // (§5.2), so leftover space is worth more as protective gap than as an empty margin.
  const gapXPx =
    columns > 1 ? Math.max(minGapPx, (availableWidthPx - keyPx * columns) / (columns - 1)) : 0;

  return {
    keyPx,
    gapXPx,
    gapYPx: minGapPx,
    clampedBy,
    maxKeyMm: Math.min(fitWidthPx, fitHeightPx) / pxPerMm,
  };
}

/**
 * Width of a key covering `span` columns — the cells it stands on **plus the gaps between them**.
 *
 * Forgetting the gaps is the whole reason this is a named function: a two-column key that is only two
 * keys wide leaves a hairline of dead space inside the row, and the row then no longer fills the width
 * `keyboardMetrics` divided up. With the gaps included, a spanned row measures exactly the same as an
 * unspanned one — which is why `keyboardMetrics` needs to know nothing about spans at all.
 */
export function keyWidthPx(span: number, metrics: KeyboardMetrics): number {
  return metrics.keyPx * span + metrics.gapXPx * (span - 1);
}

/**
 * The grid a board is drawn into — **always the letter board's, never the layer's** (`GEO-03`, ADR-039).
 *
 * This exists as a named function with one caller so that the rule has somewhere to be asserted. Passing
 * `rows: board.rows.length` at the call site is the obvious code and it is wrong: the digit layer has two rows
 * where its letter board has three, `keyboardMetrics` takes `min(requested, width fit, height fit)`, and a
 * two-row board asked about its own two rows gets a larger height fit. At ten columns height is what binds and
 * low-effort mode drives `keyMm` to the layout's own ceiling, so in exactly that configuration the honest
 * measurement hands back bigger keys and slides the grid out from under a hand that has learned it.
 *
 * The signature takes the letter board rather than the board being drawn for the same reason: there is no way
 * to call it with the layer by mistake.
 */
export function boardGrid(letterBoard: { columns: number; rows: readonly unknown[] }): {
  columns: number;
  rows: number;
} {
  return { columns: letterBoard.columns, rows: letterBoard.rows.length };
}

/**
 * Width of the content band in px. Search-driven UI lives inside it; keys may span the full
 * width because they are recall, not search (§5.2). Unused pixels are cheaper than unseen
 * buttons.
 */
export function contentBandPx(viewportWidthPx: number, contentBandPct: number): number {
  return (viewportWidthPx * contentBandPct) / 100;
}

/**
 * How many targets of at least `minTargetMm` fit in one row of the band.
 *
 * This is the arithmetic that caught the bottom-bar problem in the plan (§0.1): at key size
 * the 60 % band holds six targets, not the ten the merged bar needed. Exported so a layout
 * can assert its own feasibility instead of discovering it on the device.
 */
export function targetsPerBandRow(args: {
  viewportWidthPx: number;
  contentBandPct: number;
  targetMm: number;
  gapPx: number;
  pxPerMm: number;
}): number {
  const bandPx = contentBandPx(args.viewportWidthPx, args.contentBandPct);
  const targetPx = args.targetMm * args.pxPerMm;
  if (targetPx <= 0) return 0;
  return Math.floor((bandPx + args.gapPx) / (targetPx + args.gapPx));
}

/**
 * The height and width of one Sätze tile, for the grid that renders them (`GEO-23`, ADR-044).
 *
 * Pure, and asserted rather than eyeballed for the reason `GEO-23` records: **the container renders the
 * configuration that breaks the device at 17.4 mm and passes.** WebKit here has no safe-area insets and an
 * 820 px viewport, so a bounding-box measurement is measuring the wrong device — the arithmetic has to be
 * checked against `AIR4_USABLE_HEIGHT` instead.
 *
 * `columns` and `rows` are arguments rather than imports from `core/authoring/capacity.ts`: geometry must not
 * depend on authoring, and the reconciliation between the two belongs in a test, exactly as `LAYOUT_COLUMNS`
 * is reconciled with each layout's own declared column count (`GEO-03`).
 *
 * **The audio banner is the interesting input.** It is a flex child of the same column as the surface, so
 * when it appears it takes `--min-target` plus a band gap out of the height the grid has — and the tiles do
 * not shrink to compensate, because each carries `minHeight: var(--min-target)`. They overflow onto the bar
 * below instead, which is two activation targets on top of each other and one of them is `Ja`. Whether the
 * banner is up is therefore part of the geometry, not a detail (C84).
 */
export function saetzeTileMm(args: {
  usableHeightPx?: number;
  viewportWidthPx: number;
  contentBandPct: number;
  columns: number;
  rows: number;
  keyGapMm: number;
  pxPerMm: number;
  bannerShown: boolean;
}): { widthMm: number; heightMm: number } {
  const {
    usableHeightPx = AIR4_USABLE_HEIGHT,
    viewportWidthPx,
    contentBandPct,
    columns,
    rows,
    keyGapMm,
    pxPerMm,
    bannerShown,
  } = args;

  const gapPx = keyGapMm * pxPerMm;
  const minTargetPx = MIN_TARGET_MM * pxPerMm;

  // `.app-shell` is a flex column with `--band-gap` between its children: the output bar, the surface and
  // the bottom bar — plus the banner when it is up, which adds a fourth child and therefore a third gap.
  const bottomBarPx = 2 * BAND_HEIGHTS.bottomRow + BAND_HEIGHTS.divider;
  const gaps = bannerShown ? 3 : 2;
  const bannerPx = bannerShown ? minTargetPx : 0;
  const surfacePx =
    usableHeightPx - BAND_HEIGHTS.output - bottomBarPx - gaps * BAND_HEIGHTS.gap - bannerPx;

  // `SurfaceGrid` pads by `--band-gap` top and bottom and puts `--key-gap` between rows.
  const rowsHeightPx = surfacePx - 2 * BAND_HEIGHTS.gap - gapPx * Math.max(0, rows - 1);
  const bandWidthPx = viewportWidthPx * (contentBandPct / 100);
  const columnsWidthPx = bandWidthPx - gapPx * Math.max(0, columns - 1);

  return {
    widthMm: columnsWidthPx / columns / pxPerMm,
    heightMm: rowsHeightPx / rows / pxPerMm,
  };
}
